import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/api';
import { SnackbarContext } from '../App';
import {
    Plus, Building2, Columns, LayoutList,
    Archive, Briefcase, Search, ChevronRight
} from 'lucide-react';
import useListContext from '../hooks/useListContext';
import SavedViewBar from './SavedViewBar';
import OpportunityKanban from './OpportunityKanban';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import DataTable from './ui/DataTable';
import StatusBadge from './ui/StatusBadge';
import ScoreBadge from './ui/ScoreBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";

const STAGES = ['NEW', 'QUALIFYING', 'MEETING', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'];
const STATUSES = ['OPEN', 'WON', 'LOST', 'ARCHIVED'];

const STATUS_VARIANTS = {
    OPEN: 'primary', WON: 'success', LOST: 'destructive', ARCHIVED: 'default'
};

const formatCurrency = (val, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(val || 0);

export default function Opportunities() {
    const navigate = useNavigate();
    const showSnackbar = useContext(SnackbarContext);
    const [opportunities, setOpportunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('list');

    // Filters & Search
    const [searchParams] = useSearchParams();
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'OPEN');
    const [filterStage, setFilterStage] = useState(searchParams.get('stage') || 'ALL');

    // Pagination & Sort
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    // Selection
    const [selectedIds, setSelectedIds] = useState([]);

    // Summary stats
    const [summary, setSummary] = useState(null);

    // savedViewBar
    const listContext = useListContext({
        moduleType: 'OPPORTUNITIES',
        defaultFilters: { filterStatus: 'OPEN', filterStage: 'ALL' },
        defaultSort: { field: 'createdAt', order: 'desc' }
    });

    const handleApplyView = (view) => {
        listContext.applyView(view);
        const fs = view.filterState instanceof Map ? Object.fromEntries(view.filterState) : (view.filterState || {});
        if (fs.filterStatus !== undefined) setFilterStatus(fs.filterStatus);
        if (fs.filterStage !== undefined) setFilterStage(fs.filterStage);
        const ss = view.sortState instanceof Map ? Object.fromEntries(view.sortState) : (view.sortState || {});
        if (ss.field) setSortBy(ss.field);
        if (ss.order) setSortOrder(ss.order);
        setPage(1);
    };

    const handleClearAllFilters = () => {
        listContext.clearFilters();
        setFilterStatus('OPEN');
        setFilterStage('ALL');
        setSearch('');
        setSortBy('createdAt');
        setSortOrder('desc');
        setPage(1);
    };

    useEffect(() => {
        listContext.setFilters({ filterStatus, filterStage });
    }, [filterStatus, filterStage]);

    useEffect(() => {
        listContext.setSort({ field: sortBy, order: sortOrder });
    }, [sortBy, sortOrder]);

    const hasActiveFilters = filterStatus !== 'OPEN' || filterStage !== 'ALL' || search !== '';

    // ─── Data Fetching ───
    const fetchOpportunities = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page, limit: 25, sortBy, sortOrder
            });
            if (filterStatus && filterStatus !== 'ALL_STATUSES') params.append('status', filterStatus);
            if (filterStage && filterStage !== 'ALL') params.append('stage', filterStage);
            if (search) params.append('search', search);

            // Dashboard Drilldown Filters
            if (searchParams.get('isOverdue')) params.append('isOverdue', 'true');
            if (searchParams.get('isStale')) params.append('isStale', 'true');
            if (searchParams.get('ownerId')) params.append('ownerId', searchParams.get('ownerId'));

            const res = await api.get(`/api/opportunities?${params.toString()}`);
            setOpportunities(res.data.opportunities);
            setTotalPages(res.data.pagination.pages || 1);
            setTotalRecords(res.data.pagination.total || 0);
        } catch (err) {
            console.error('Fetch opportunities error:', err);
            showSnackbar(err.isAppError ? err.message : 'Failed to fetch opportunities', 'error');
        } finally {
            setLoading(false);
        }
    }, [page, filterStatus, filterStage, search, sortBy, sortOrder, showSnackbar, searchParams]);

    const fetchSummary = async () => {
        try {
            const res = await api.get('/api/opportunities/summary');
            setSummary(res.data);
        } catch (_) { /* silent */ }
    };

    useEffect(() => { fetchOpportunities(); }, [fetchOpportunities]);
    useEffect(() => { fetchSummary(); }, []);

    // ─── Navigation ───
    const openCreatePage = () => navigate('/opportunities/new');
    const goToDetail = (id) => navigate(`/opportunities/${id}`);

    // ─── Stage Update ───
    const handleUpdateStage = async (id, stage) => {
        try {
            await api.put(`/api/opportunities/${id}`, { stage });
            showSnackbar('Stage updated');
            fetchOpportunities();
            fetchSummary();
        } catch (err) {
            showSnackbar(err.isAppError ? err.message : 'Failed to update stage', 'error');
        }
    };

    // ─── Bulk Actions ───
    const handleBulkStageChange = async (newStage) => {
        try {
            const res = await api.post('/api/opportunities/bulk-update', { ids: selectedIds, updates: { stage: newStage } });
            showSnackbar(`${res.data.success} updated, ${res.data.failed} failed`);
            setSelectedIds([]);
            fetchOpportunities();
            fetchSummary();
        } catch (err) {
            showSnackbar(err.isAppError ? err.message : 'Bulk update failed', 'error');
        }
    };

    const handleBulkArchive = async () => {
        try {
            const res = await api.post('/api/opportunities/bulk-update', { ids: selectedIds, updates: { archive: true } });
            showSnackbar(`${res.data.success} archived, ${res.data.failed} failed`);
            setSelectedIds([]);
            fetchOpportunities();
            fetchSummary();
        } catch (err) {
            showSnackbar(err.isAppError ? err.message : 'Bulk archive failed', 'error');
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        else { setSortBy(field); setSortOrder('desc'); }
        setPage(1);
    };

    return (
        <PageShell className="bg-background">
            <div className="p-8 space-y-8">
                <PageHeader
                    title="Revenue Pipeline"
                    subtitle={
                        searchParams.get('isOverdue') ? "Viewing Overdue deals requiring attention." :
                        searchParams.get('isStale') ? "Viewing Stale deals requiring activity." :
                        "Manage your revenue pipeline, deal health, and expected growth."
                    }
                    icon={Briefcase}
                    actions={
                        <div className="flex items-center gap-3">
                            {(searchParams.get('isOverdue') || searchParams.get('isStale')) && (
                                <Button variant="outline" onClick={() => navigate('/opportunities?status=OPEN')} className="rounded-2xl border-dashed border-red-300 text-red-600 hover:bg-red-50 gap-2">
                                    Clear Health Filter
                                </Button>
                            )}
                            <Button onClick={openCreatePage}
                                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl border-0 gap-2 h-12 px-8 rounded-2xl font-black uppercase tracking-widest text-xs transition-all active:scale-95">
                                <Plus className="w-5 h-5" /> New Opportunity
                            </Button>
                        </div>
                    }
                />

                {/* Pipeline Summary Stats */}
                {summary && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="bg-muted/30 border border-border/50 rounded-3xl p-6 flex flex-col shadow-sm group hover:border-primary/20 transition-all">
                            <span className="text-3xl font-black text-foreground tabular-nums">{summary.pipeline.totalDeals}</span>
                            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mt-1 opacity-70">Active Pipeline</span>
                            <div className="mt-2 text-[11px] font-bold text-primary">{formatCurrency(summary.pipeline.totalValue)} volume</div>
                        </div>
                        <div className="bg-success/5 border border-success/20 rounded-3xl p-6 flex flex-col shadow-sm group hover:border-success/40 transition-all">
                            <span className="text-3xl font-black text-success tabular-nums">{summary.won.totalDeals}</span>
                            <span className="text-[10px] font-black text-success uppercase tracking-widest mt-1 opacity-70">Won Revenue</span>
                            <div className="mt-2 text-[11px] font-bold text-success/80">{formatCurrency(summary.won.totalValue)} closed</div>
                        </div>
                        <div className="bg-warning/5 border border-warning/20 rounded-3xl p-6 flex flex-col shadow-sm group hover:border-warning/40 transition-all">
                            <span className="text-3xl font-black text-warning tabular-nums">
                                {(summary.stageDistribution?.NEGOTIATION?.count || 0) + (summary.stageDistribution?.PROPOSAL?.count || 0)}
                            </span>
                            <span className="text-[10px] font-black text-warning uppercase tracking-widest mt-1 opacity-70">At Risk / Closing</span>
                            <div className="mt-2 text-[11px] font-bold text-warning/80">
                                {formatCurrency((summary.stageDistribution?.NEGOTIATION?.value || 0) + (summary.stageDistribution?.PROPOSAL?.value || 0))} volume
                            </div>
                        </div>
                        <div className="bg-primary/5 border border-primary/20 rounded-3xl p-6 flex flex-col shadow-sm group hover:border-primary/40 transition-all">
                            <span className="text-3xl font-black text-primary tabular-nums">
                                {summary.stageDistribution?.NEW?.count || 0}
                            </span>
                            <span className="text-[10px] font-black text-primary uppercase tracking-widest mt-1 opacity-70">Unqualified Leads</span>
                            <div className="mt-2 text-[11px] font-bold text-primary/80">
                                {formatCurrency(summary.stageDistribution?.NEW?.value || 0)} potential
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-card border border-border rounded-[2.5rem] shadow-sm overflow-hidden mb-8">
                    <div className="px-8 py-6 border-b border-border bg-muted/5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground opacity-50" />
                                <input 
                                    type="text" 
                                    placeholder="Search by title or company..."
                                    value={search}
                                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                    className="w-full bg-background border-2 border-border/50 pl-11 pr-4 py-2.5 rounded-2xl text-sm font-bold focus:border-primary outline-none transition-all"
                                />
                            </div>
                            <div className="flex items-center h-11 border-2 border-border/50 rounded-2xl overflow-hidden bg-background">
                                <button onClick={() => setViewMode('list')}
                                    className={`flex items-center gap-1.5 px-5 h-full text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'list' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted/30'}`}>
                                    <LayoutList className="w-3.5 h-3.5" /> List
                                </button>
                                <button onClick={() => setViewMode('kanban')}
                                    className={`flex items-center gap-1.5 px-5 h-full text-[10px] font-black uppercase tracking-widest transition-all ${viewMode === 'kanban' ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted/30'}`}>
                                    <Columns className="w-3.5 h-3.5" /> Board
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="w-[160px]">
                                <Select value={filterStatus} onValueChange={(val) => { setFilterStatus(val); setPage(1); }}>
                                    <SelectTrigger className="w-full h-11 bg-background border-2 border-border/50 text-[11px] font-black uppercase tracking-widest rounded-2xl px-5 focus:ring-primary focus:border-primary">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-border shadow-xl">
                                        <SelectItem value="ALL_STATUSES" className="text-xs font-bold">All Statuses</SelectItem>
                                        {STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs font-bold">{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="w-[160px]">
                                <Select value={filterStage} onValueChange={(val) => { setFilterStage(val); setPage(1); }}>
                                    <SelectTrigger className="w-full h-11 bg-background border-2 border-border/50 text-[11px] font-black uppercase tracking-widest rounded-2xl px-5 focus:ring-primary focus:border-primary">
                                        <SelectValue placeholder="Stage" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-border shadow-xl">
                                        <SelectItem value="ALL" className="text-xs font-bold">All Stages</SelectItem>
                                        {STAGES.map(s => <SelectItem key={s} value={s} className="text-xs font-bold">{s}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 py-2">
                            <SavedViewBar
                            savedViews={listContext.savedViews}
                            activeViewId={listContext.activeViewId}
                            viewsLoading={listContext.viewsLoading}
                            onApplyView={handleApplyView}
                            onSaveView={(name) => listContext.saveCurrentView(name)}
                            onDeleteView={listContext.deleteView}
                            onClearFilters={handleClearAllFilters}
                            hasActiveFilters={hasActiveFilters}
                        />
                    </div>

                    {viewMode === 'list' && (
                        <DataTable
                            columns={[
                                {
                                    header: 'Opportunity',
                                    accessor: 'title',
                                    sortable: true,
                                    render: (opp) => (
                                        <div className="flex flex-col cursor-pointer group/item" onClick={() => goToDetail(opp._id)}>
                                            <span className="text-sm font-bold text-foreground group-hover/item:text-primary transition-colors">{opp.title}</span>
                                            <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-muted-foreground font-bold uppercase tracking-widest opacity-60">
                                                {(opp.companyId?.name || opp.companyMasterId?.name) ? (
                                                    <><Building2 className="w-3 h-3" /> {opp.companyId?.name || opp.companyMasterId?.name}</>
                                                ) : <span className="italic normal-case">No company</span>}
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    header: 'Stage',
                                    accessor: 'stage',
                                    sortable: true,
                                    render: (opp) => (
                                        <div className="flex flex-col gap-1.5 items-start">
                                            <select
                                                value={opp.stage}
                                                onChange={(e) => { e.stopPropagation(); handleUpdateStage(opp._id, e.target.value); }}
                                                onClick={e => e.stopPropagation()}
                                                className="text-[10px] font-black uppercase tracking-widest rounded-lg px-2.5 py-1 border shadow-sm outline-none cursor-pointer bg-background hover:bg-muted/30 transition-all font-sans"
                                            >
                                                {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                            {opp.status !== 'OPEN' && <StatusBadge variant={STATUS_VARIANTS[opp.status]}>{opp.status}</StatusBadge>}
                                        </div>
                                    )
                                },
                            {
                                header: 'Score',
                                accessor: 'intelligence',
                                render: (opp) => <ScoreBadge intelligence={opp.intelligence} label="Deal" />
                            },
                            {
                                header: 'Value',
                                accessor: 'expectedValue',
                                sortable: true,
                                render: (opp) => (
                                    <span className="text-sm font-bold text-foreground tabular-nums">
                                        {formatCurrency(opp.expectedValue, opp.currency)}
                                    </span>
                                )
                            },
                            {
                                header: 'Close Date',
                                accessor: 'expectedCloseDate',
                                sortable: true,
                                render: (opp) => {
                                    if (!opp.expectedCloseDate) return <span className="text-muted-foreground text-xs">—</span>;
                                    const d = new Date(opp.expectedCloseDate);
                                    return (
                                        <div className="text-xs font-medium text-muted-foreground">
                                            {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                    );
                                }
                            },
                            {
                                header: '',
                                accessor: '_id',
                                align: 'right',
                                render: (opp) => (
                                    <Button variant="ghost" size="sm" onClick={() => goToDetail(opp._id)}
                                        className="h-8 w-8 p-0 rounded-full hover:bg-primary/10 hover:text-primary transition-all">
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                )
                            }
                        ]}
                        data={opportunities}
                        loading={loading}
                        pagination={{ page, limit: 25, totalRecords, totalPages }}
                        onPageChange={setPage}
                        sort={{ sortBy, sortOrder }}
                        onSort={handleSort}
                        selectable={{
                            selectedIds,
                            onSelectionChange: setSelectedIds,
                            idAccessor: '_id'
                        }}
                        bulkActions={[
                            { label: 'Move to Proposal', onClick: () => handleBulkStageChange('PROPOSAL') },
                            { label: 'Mark Won', variant: 'primary', onClick: () => handleBulkStageChange('WON') },
                            { label: 'Mark Lost', onClick: () => handleBulkStageChange('LOST') },
                            { label: 'Archive', icon: Archive, onClick: handleBulkArchive },
                        ]}
                        />
                    )}

                    {viewMode === 'kanban' && (
                        <div className="p-4 bg-muted/5 min-h-[600px]">
                            <OpportunityKanban
                                opportunities={opportunities}
                                loading={loading}
                                onStageMove={handleUpdateStage}
                                onCardClick={goToDetail}
                            />
                        </div>
                    )}
                </div>
            </div>
        </PageShell>
    );
}
